'use client';

/*
 * Ported from mstreetinvest@cec8be916:resources/js/components/crm-chat/cards/row-list.tsx (PORTING.md §1.2).
 * Edits: links are next/link; the 2px corners read --chat-radius, so each console theme owns them
 * (app/(admin)/admin-theme.css §1b — Ledger's is mStreet's 2px); a RowList can be named for a screen reader; and
 * the focus rings name their style (focus-visible:outline-solid) — under Tailwind 4 mStreet's outline-none left
 * focus-visible:outline-2 drawing nothing (chat/__tests__/focus-ring.guard.spec.ts).
 */
import { cn } from '@/lib/utils';
import Link from 'next/link';
import { ArrowRight, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { initialsOf } from './format';

/*
 * The one row language every chat list speaks: a 28px mark (logo or initials), the name with its chips, one muted
 * meta line, and a right-aligned secondary figure. Rows with a destination are links, so they open in a new tab too.
 */

/**
 * A white bordered panel with an optional header strip, holding rows or sections. `title` names what the panel
 * shows (charts, tables); `caption` is the tool's one-line summary above a list.
 */
export function CardPanel({
    title,
    caption,
    aside,
    footer,
    children,
    className,
}: {
    title?: ReactNode;
    caption?: ReactNode;
    aside?: ReactNode;
    footer?: ReactNode;
    children: ReactNode;
    className?: string;
}) {
    return (
        <div
            className={cn(
                '@container w-full overflow-hidden rounded-[var(--chat-radius)] border border-border bg-card text-card-foreground',
                className,
            )}
        >
            {(title || caption || aside) && (
                <div className="flex min-h-8 items-center justify-between gap-3 border-b border-border px-3 py-1.5">
                    {title ? (
                        <p className="min-w-0 text-sm font-medium text-foreground">
                            {title}
                        </p>
                    ) : caption ? (
                        <p className="min-w-0 truncate text-xs font-medium text-muted-foreground">
                            {caption}
                        </p>
                    ) : (
                        <span />
                    )}
                    {aside}
                </div>
            )}
            {children}
            {footer}
        </div>
    );
}

export function RowList({
    children,
    'aria-label': label,
}: {
    children: ReactNode;
    /** What the rows are, for a screen reader (the card's title when its strip shows the caption). */
    'aria-label'?: string;
}) {
    return (
        <ul aria-label={label} className="divide-y divide-border">
            {children}
        </ul>
    );
}

const rowClass =
    'group/row flex w-full items-start gap-2.5 px-3 py-2 text-start';

export function ListRow({
    href,
    mark,
    title,
    chips,
    meta,
    trailing,
    children,
}: {
    href?: string | null;
    mark?: ReactNode;
    title: ReactNode;
    chips?: ReactNode;
    meta?: ReactNode;
    trailing?: ReactNode;
    children?: ReactNode;
}) {
    const body = (
        <>
            {mark}
            <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="min-w-0 truncate text-sm font-medium text-foreground">
                        {title}
                    </span>
                    {chips}
                </div>
                {meta && (
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {meta}
                    </p>
                )}
                {children}
            </div>
            {(trailing || href) && (
                <div className="flex shrink-0 items-center gap-2 pt-0.5">
                    {trailing}
                    {href && (
                        <ArrowRight
                            aria-hidden="true"
                            className="size-3.5 text-muted-foreground opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-visible/row:opacity-100 rtl-flip"
                        />
                    )}
                </div>
            )}
        </>
    );

    return (
        <li>
            {href ? (
                <Link
                    href={href}
                    className={cn(
                        rowClass,
                        'transition-colors outline-none hover:bg-accent focus-visible:bg-accent focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-2 focus-visible:outline-ring',
                    )}
                >
                    {body}
                </Link>
            ) : (
                <div className={rowClass}>{body}</div>
            )}
        </li>
    );
}

/** Right-aligned secondary figure on a row. */
export function RowFigure({
    children,
    strong = false,
    title,
}: {
    children: ReactNode;
    strong?: boolean;
    title?: string;
}) {
    return (
        <span
            title={title}
            className={cn(
                'text-xs whitespace-nowrap tabular-nums',
                strong
                    ? 'font-medium text-foreground'
                    : 'text-muted-foreground',
            )}
        >
            {children}
        </span>
    );
}

/**
 * True when a logo "loaded" but carries nothing: DuckDuckGo's icon service answers unknown domains with a 404 whose
 * body is a 48px grey chevron, which an `<img>` renders without firing `error`; a 1px image is a tracking stub.
 */
function isEmptyLogo(image: HTMLImageElement): boolean {
    if (image.naturalWidth <= 1 || image.naturalHeight <= 1) {
        return true;
    }

    try {
        return (
            new URL(image.currentSrc || image.src).hostname ===
                'icons.duckduckgo.com' &&
            image.naturalWidth === 48 &&
            image.naturalHeight === 48
        );
    } catch {
        return false;
    }
}

/**
 * A company logo, or initials when there is no logo or it fails to load. Logos sit on a white tile in both themes
 * so dark marks stay visible; people get the round avatar, organisations a square tile.
 */
export function EntityMark({
    name,
    logoUrl,
    shape = 'square',
    size = 'md',
}: {
    name: string | null | undefined;
    logoUrl?: string | null;
    shape?: 'square' | 'round';
    size?: 'md' | 'lg';
}) {
    const [failedUrl, setFailedUrl] = useState<string | null>(null);
    const showLogo = Boolean(logoUrl) && failedUrl !== logoUrl;
    const box = size === 'lg' ? 'size-9' : 'size-7';

    if (showLogo && logoUrl) {
        return (
            <span
                aria-hidden="true"
                className={cn(
                    'flex shrink-0 items-center justify-center overflow-hidden rounded-[var(--chat-radius)] border border-border bg-white',
                    box,
                )}
            >
                <img
                    src={logoUrl}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    // Covers images that settled before React attached its handlers (server-rendered pages).
                    ref={(image) => {
                        if (
                            image?.complete &&
                            (image.naturalWidth === 0 || isEmptyLogo(image))
                        ) {
                            setFailedUrl(logoUrl);
                        }
                    }}
                    onError={() => setFailedUrl(logoUrl)}
                    onLoad={(event) => {
                        if (isEmptyLogo(event.currentTarget)) {
                            setFailedUrl(logoUrl);
                        }
                    }}
                    className="size-full object-contain p-0.5"
                />
            </span>
        );
    }

    return (
        <span
            aria-hidden="true"
            className={cn(
                'flex shrink-0 items-center justify-center border border-border bg-muted font-semibold text-foreground select-none',
                size === 'lg' ? 'text-xs' : 'text-[11px]',
                shape === 'round' ? 'rounded-full' : 'rounded-[var(--chat-radius)]',
                box,
            )}
        >
            {initialsOf(name)}
        </span>
    );
}

/** A square tile with an icon, for rows that are not a company or a person (groups, campaigns, emails). */
export function IconMark({ icon: Icon }: { icon: LucideIcon }) {
    return (
        <span
            aria-hidden="true"
            className="flex size-7 shrink-0 items-center justify-center rounded-[var(--chat-radius)] border border-border bg-muted text-muted-foreground"
        >
            <Icon className="size-3.5" />
        </span>
    );
}

export type ChipTone =
    | 'neutral'
    | 'strong'
    | 'outline'
    | 'positive'
    | 'caution'
    | 'negative'
    | 'hot'
    | 'warm'
    | 'cold';

const CHIP_TONES: Record<ChipTone, string> = {
    neutral: 'bg-muted text-muted-foreground',
    strong: 'bg-foreground text-background',
    outline: 'border border-foreground/70 text-foreground',
    positive: 'bg-sent-positive-bg text-sent-positive',
    caution: 'bg-sent-mixed-bg text-sent-mixed',
    negative: 'bg-sent-negative-bg text-sent-negative',
    hot: 'bg-temp-hot-bg text-temp-hot',
    warm: 'bg-temp-warm-bg text-temp-warm',
    cold: 'bg-temp-cold-bg text-temp-cold',
};

/** Square status chip: sentence-case word on a token tint. */
export function Chip({
    tone = 'neutral',
    title,
    children,
}: {
    tone?: ChipTone;
    title?: string;
    children: ReactNode;
}) {
    return (
        <span
            title={title}
            className={cn(
                'inline-flex h-5 shrink-0 items-center gap-1 px-1.5 text-xs leading-none font-medium whitespace-nowrap',
                CHIP_TONES[tone],
            )}
        >
            {children}
        </span>
    );
}

const STATUS_TONES: Record<string, ChipTone> = {
    customer: 'positive',
    interested: 'positive',
    won: 'positive',
    'closed won': 'positive',
    bounced: 'negative',
    unsubscribed: 'negative',
    'not interested': 'negative',
    lost: 'negative',
    'closed lost': 'negative',
    churned: 'negative',
    failed: 'negative',
    paused: 'caution',
    'pending approval': 'caution',
    sent: 'positive',
    delivered: 'positive',
    replied: 'positive',
    queued: 'caution',
    sending: 'caution',
    cancelled: 'negative',
};

/** Record status (company, contact, deal stage). Quiet by default; only clear wins and losses carry a tint. */
export function StatusChip({
    status,
    tone,
}: {
    status: string | null | undefined;
    /** Overrides the tone read from the label (a deal's custom stage name with a known won/lost outcome). */
    tone?: ChipTone;
}) {
    if (!status) {
        return null;
    }

    const label = status.replace(/_/g, ' ');
    // Sentence case ("Not Interested" → "Not interested"); all-caps words such as "VIP" stay as written.
    const text = label
        .split(' ')
        .map((word, index) =>
            index === 0
                ? word.charAt(0).toUpperCase() + word.slice(1)
                : word === word.toUpperCase()
                  ? word
                  : word.toLowerCase(),
        )
        .join(' ');

    return (
        <Chip tone={tone ?? STATUS_TONES[label.toLowerCase()] ?? 'neutral'}>
            {text}
        </Chip>
    );
}

/** A quiet link line at the foot of a panel ("View all 12 on the board"). */
export function PanelLink({
    href,
    children,
}: {
    href: string;
    children: ReactNode;
}) {
    return (
        <Link
            href={href}
            className="group/link flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-xs font-medium text-foreground transition-colors outline-none hover:bg-accent focus-visible:bg-accent focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-2 focus-visible:outline-ring"
        >
            <span className="min-w-0 truncate">{children}</span>
            <ArrowRight
                aria-hidden="true"
                className="size-3.5 shrink-0 text-muted-foreground transition-transform group-hover/link:translate-x-0.5 rtl-flip [&:dir(rtl)]:group-hover/link:-translate-x-0.5"
            />
        </Link>
    );
}
